import { useEffect, useMemo } from "react"

// ** Third Party Components
import axios from "axios"
import { parseCookies } from "nookies"
import { useInfiniteQuery } from "@tanstack/react-query"
import { useNavigate, useSearchParams } from "react-router-dom"

// ** UI Components
import PmsLoader from "@components/common/loader"
import { Card, CardContent } from "@components/common/card"
import { ScrollArea } from "@components/common/scroll-area"

// ** Store
import { useAuth } from "@stores/auth-store"

// ** Types
import { AppNotification, DictType } from "@pma/types"
// ** API Routes
import { GET_ACTIVITIES_LIST, SERVER_URL } from "@pma/utils/api-routes"

// ** Utils
import { cn } from "@pma/utils/helpers"
import { ACCESS_TOKEN } from "@pma/utils/constants"

// ** Custom Hooks
import useTranslator from "@hooks/use-translator"

// ** Custom Components
import TaskActivityCard from "./task-activity-card"

interface Props {
  task: DictType
}
export function TaskActivity({ task }: Props) {
  const navigate = useNavigate()
  const { authConfig } = useAuth()
  const [searchParam] = useSearchParams()
  const { translate, locale } = useTranslator()

  const isHebrew = locale === "he"
  const queryKey = searchParam.get("revalidate")
  const orgId = authConfig?.last_organization_id
  const accessToken = parseCookies()[ACCESS_TOKEN]

  const getTaskLogs = async (payload: any) => {
    const response = await axios.post(SERVER_URL + GET_ACTIVITIES_LIST, payload, {
      headers: { Authorization: `Bearer ${accessToken}` },
    })

    return response.data
  }

  const {
    data: activities,
    refetch: refetchLogs,
    isLoading,
  } = useInfiniteQuery({
    queryKey: ["task-logs-notifications", task?.id],
    queryFn: ({ pageParam = 1 }) =>
      getTaskLogs({
        paging_per_page: 30,
        paging_start_page: pageParam,
        organization_id: orgId,
        task_id: task?.id,
        type: "all",
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage?.nextPage) {
        return undefined
      }
      return allPages.length + 1
    },
  })

  const formatter = new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  })

  const activitiesData = useMemo(() => {
    const temp: DictType = {}

    activities &&
      activities?.pages?.map((page: any) =>
        page?.items?.map((notification: AppNotification, index: number) => {
          const notificationDate = formatter.format(notification?.created_at)
          if (temp[notificationDate]) {
            return temp[notificationDate].push(notification)
          } else {
            return (temp[notificationDate] = [notification])
          }
        }),
      )

    return temp
  }, [isLoading])

  useEffect(() => {
    // Revalidate project when tasks and project is updated
    if (queryKey === "projects" || queryKey === "tasks") {
      refetchLogs()
      navigate(`${location.pathname}`)
    }
  }, [queryKey])

  return (
    <Card className='max-h-[95vh] border-none shadow-none'>
      {isLoading ? (
        <PmsLoader />
      ) : (
        <CardContent className='gap-6 p-0'>
          {Object.keys(activitiesData).length < 1 ? (
            <p
              className={cn(
                "text-md mb-4 flex h-44 items-center justify-center font-medium text-muted-foreground",
                isHebrew ? "rtl" : "ltr",
              )}
            >
              {translate("Sorry we didn't find anything")}.
            </p>
          ) : (
            <ScrollArea className='h-[80vh] p-1 pb-12'>
              {Object.keys(activitiesData).map((day: any, index) => (
                <div className='flex h-full w-full flex-col overflow-y-auto'>
                  {activitiesData[day].map((activity: DictType, idx: number) => (
                    <>
                      <TaskActivityCard
                        key={idx}
                        activity={activity}
                        reverse={index % 2 === 1}
                        task={task}
                      />
                      <span className='h-2' />
                    </>
                  ))}
                </div>
              ))}
            </ScrollArea>
          )}
        </CardContent>
      )}
    </Card>
  )
}
